/**
 * Carries a search result across a navigation: "Go to Documents" stores the
 * task, and the Documents ⓘ opens its dialog with that task expanded once the
 * page has mounted. In memory and short-lived — a stale request (the page
 * never mounted, the reader went elsewhere) must not pop a dialog later.
 */
import type { PageInfoId } from "@/lib/i18n/types/pageInfo";

const MAX_AGE_MS = 15_000;

let pending: { page: PageInfoId; item: string; at: number } | null = null;

export function requestPageInfoOpen(page: PageInfoId, item: string): void {
  pending = { page, item, at: Date.now() };
}

/**
 * The task waiting for this page, without consuming it. Pages often mount
 * their title twice while loading (skeleton, then content), so the request
 * must survive a mount that unmounts before it opens anything.
 */
export function peekPageInfoOpen(page: PageInfoId): string | null {
  if (!pending || pending.page !== page) return null;
  if (Date.now() - pending.at > MAX_AGE_MS) {
    pending = null;
    return null;
  }
  return pending.item;
}

/** The task to expand if this page has a fresh request waiting; consumes it. */
export function takePageInfoOpen(page: PageInfoId): string | null {
  if (!pending || pending.page !== page) return null;
  const { item, at } = pending;
  pending = null;
  return Date.now() - at <= MAX_AGE_MS ? item : null;
}
